/**
 * A SMALL, typed fixture dataset for unit tests + bootstrapping the UI before the
 * real ingest runs. Covers the three Kanto starters and a couple of others.
 *
 * NOT the real dataset — the ingest writes Gen 1–3 to data/generated/ (D5). These
 * values follow the real games closely enough for tests but are hand-authored;
 * never treat them as the source of truth at runtime.
 */
import type { SpeciesData, MoveData } from '@/shared';

export const FIXTURE_MOVES: readonly MoveData[] = [
  {
    id: 'tackle',
    name: 'Tackle',
    type: 'normal',
    category: 'physical',
    power: 40,
    accuracy: 100,
    pp: 35,
    priority: 0,
    target: 'opponent',
  },
  {
    id: 'vine-whip',
    name: 'Vine Whip',
    type: 'grass',
    category: 'physical',
    power: 45,
    accuracy: 100,
    pp: 25,
    priority: 0,
    target: 'opponent',
  },
  {
    id: 'ember',
    name: 'Ember',
    type: 'fire',
    category: 'special',
    power: 40,
    accuracy: 100,
    pp: 25,
    priority: 0,
    target: 'opponent',
    effects: [{ kind: 'inflictStatus', chance: 10, params: { status: 'burn' } }],
  },
  {
    id: 'water-gun',
    name: 'Water Gun',
    type: 'water',
    category: 'special',
    power: 40,
    accuracy: 100,
    pp: 25,
    priority: 0,
    target: 'opponent',
  },
  {
    id: 'growl',
    name: 'Growl',
    type: 'normal',
    category: 'status',
    power: null,
    accuracy: 100,
    pp: 40,
    priority: 0,
    target: 'opponent',
    effects: [{ kind: 'statChange', params: { stat: 'atk', stages: -1 } }],
  },
  {
    id: 'thunder-shock',
    name: 'Thunder Shock',
    type: 'electric',
    category: 'special',
    power: 40,
    accuracy: 100,
    pp: 30,
    priority: 0,
    target: 'opponent',
    effects: [{ kind: 'inflictStatus', chance: 10, params: { status: 'paralysis' } }],
  },
  {
    id: 'quick-attack',
    name: 'Quick Attack',
    type: 'normal',
    category: 'physical',
    power: 40,
    accuracy: 100,
    pp: 30,
    priority: 1,
    target: 'opponent',
  },
];

export const FIXTURE_SPECIES: readonly SpeciesData[] = [
  {
    id: 'bulbasaur',
    dexNo: 1,
    name: 'Bulbasaur',
    height: 0.7,
    types: ['grass', 'poison'],
    baseStats: { hp: 45, atk: 49, def: 49, spatk: 65, spdef: 65, spe: 45 },
    abilities: { primary: 'overgrow', hidden: 'chlorophyll' },
    learnset: [
      { moveId: 'tackle', method: 'level-up', level: 1 },
      { moveId: 'growl', method: 'level-up', level: 3 },
      { moveId: 'vine-whip', method: 'level-up', level: 9 },
    ],
    evolutions: [{ toSpeciesId: 'ivysaur', trigger: 'level-up', minLevel: 16 }],
    eggGroups: ['monster', 'grass'],
    genderRatio: 0.125,
    captureRate: 45,
    baseExpYield: 64,
    growthRate: 'medium-slow',
    isLegendary: false,
    isMythical: false,
  },
  {
    id: 'charmander',
    dexNo: 4,
    name: 'Charmander',
    height: 0.6,
    types: ['fire'],
    baseStats: { hp: 39, atk: 52, def: 43, spatk: 60, spdef: 50, spe: 65 },
    abilities: { primary: 'blaze', hidden: 'solar-power' },
    learnset: [
      { moveId: 'tackle', method: 'level-up', level: 1 },
      { moveId: 'growl', method: 'level-up', level: 1 },
      { moveId: 'ember', method: 'level-up', level: 9 },
    ],
    evolutions: [{ toSpeciesId: 'charmeleon', trigger: 'level-up', minLevel: 16 }],
    eggGroups: ['monster', 'dragon'],
    genderRatio: 0.125,
    captureRate: 45,
    baseExpYield: 62,
    growthRate: 'medium-slow',
    isLegendary: false,
    isMythical: false,
  },
  {
    id: 'squirtle',
    dexNo: 7,
    name: 'Squirtle',
    height: 0.5,
    types: ['water'],
    baseStats: { hp: 44, atk: 48, def: 65, spatk: 50, spdef: 64, spe: 43 },
    abilities: { primary: 'torrent', hidden: 'rain-dish' },
    learnset: [
      { moveId: 'tackle', method: 'level-up', level: 1 },
      { moveId: 'growl', method: 'level-up', level: 3 },
      { moveId: 'water-gun', method: 'level-up', level: 9 },
    ],
    evolutions: [{ toSpeciesId: 'wartortle', trigger: 'level-up', minLevel: 16 }],
    eggGroups: ['monster', 'water1'],
    genderRatio: 0.125,
    captureRate: 45,
    baseExpYield: 63,
    growthRate: 'medium-slow',
    isLegendary: false,
    isMythical: false,
  },
  {
    id: 'pikachu',
    dexNo: 25,
    name: 'Pikachu',
    height: 0.4,
    types: ['electric'],
    baseStats: { hp: 35, atk: 55, def: 40, spatk: 50, spdef: 50, spe: 90 },
    abilities: { primary: 'static', hidden: 'lightning-rod' },
    learnset: [
      { moveId: 'thunder-shock', method: 'level-up', level: 1 },
      { moveId: 'growl', method: 'level-up', level: 1 },
      { moveId: 'quick-attack', method: 'level-up', level: 10 },
    ],
    evolutions: [{ toSpeciesId: 'raichu', trigger: 'item', itemId: 'thunder-stone' }],
    eggGroups: ['ground', 'fairy'],
    genderRatio: 0.5,
    captureRate: 190,
    baseExpYield: 112,
    growthRate: 'medium-fast',
    isLegendary: false,
    isMythical: false,
  },
  {
    id: 'haunter',
    dexNo: 93,
    name: 'Haunter',
    height: 1.6,
    types: ['ghost', 'poison'],
    baseStats: { hp: 45, atk: 50, def: 45, spatk: 115, spdef: 55, spe: 95 },
    abilities: { primary: 'levitate' },
    learnset: [{ moveId: 'tackle', method: 'level-up', level: 1 }],
    // Trade-evolution wired end-to-end in the MVP (CLAUDE.md mechanic #4).
    evolutions: [{ toSpeciesId: 'gengar', trigger: 'trade' }],
    eggGroups: ['amorphous'],
    genderRatio: 0.5,
    captureRate: 90,
    baseExpYield: 142,
    growthRate: 'medium-slow',
    isLegendary: false,
    isMythical: false,
  },
];
